/**
 * Numeração SNCR do prescritor: saldo por tipo e pedido de novos números via
 * gov.br. O login tem de ser do próprio prescritor — o SNCR numera no CPF dele.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ConfirmationNumber as NumberIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  CLOUD_API_THEME,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
  cloudApiInputFieldSx,
} from '../../pages/whatsappCloudApiTheme';
import {
  getSncrOverview,
  isSncrNotificationTipo,
  runSncrGovBrFlow,
  SncrFlowCancelled,
  SNCR_TIPOS,
  SNCR_TIPO_HINTS,
  SNCR_TIPO_LABELS,
  type SncrAuthRequestStatus,
  type SncrOverview,
  type SncrReceitaTipo,
} from '../../services/sncrService';

interface Props {
  open: boolean;
  professionalId: string;
  /** Tipos que faltam para assinar a receita atual — já vêm marcados. */
  suggestedTipos?: SncrReceitaTipo[];
  onClose: () => void;
  onCompleted?: (status: SncrAuthRequestStatus) => void;
}

const NR_QUANTITIES = [10, 20, 30, 40, 50];
const RCE_RET_MONTHLY_REQUESTS = 3;

const SncrNumberingDialog: React.FC<Props> = ({ open, professionalId, suggestedTipos, onClose, onCompleted }) => {
  const [overview, setOverview] = useState<SncrOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [result, setResult] = useState<SncrAuthRequestStatus | null>(null);
  const [selected, setSelected] = useState<Set<SncrReceitaTipo>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const abortRef = useRef<AbortController | null>(null);

  const suggestedKey = (suggestedTipos || []).join(',');

  const load = useCallback(async () => {
    if (!professionalId) return;
    setLoading(true);
    setError(null);
    try {
      setOverview(await getSncrOverview(professionalId));
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar a numeração.');
    } finally {
      setLoading(false);
    }
  }, [professionalId]);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setStatusText(null);
    setSelected(new Set((suggestedKey ? suggestedKey.split(',') : []) as SncrReceitaTipo[]));
    void load();
  }, [open, load, suggestedKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const balanceOf = useMemo(() => {
    const map = new Map<string, SncrOverview['balances'][number]>();
    (overview?.balances || []).forEach((b) => map.set(b.receita_tipo, b));
    return map;
  }, [overview]);

  const toggle = (tipo: SncrReceitaTipo) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  };

  const blockedReason = (tipo: SncrReceitaTipo): string | null => {
    if (!isSncrNotificationTipo(tipo)) {
      if (overview && !overview.platform_cnpj_configured) return 'CNPJ da plataforma não configurado no servidor';
      const requests = balanceOf.get(tipo)?.requests_this_month || 0;
      if (requests >= RCE_RET_MONTHLY_REQUESTS) return 'Limite de 3 pedidos no mês atingido';
    }
    return null;
  };

  const handleRequest = async () => {
    if (!overview || !selected.size) return;
    setWorking(true);
    setError(null);
    setResult(null);
    setStatusText('Abrindo o gov.br…');
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const status = await runSncrGovBrFlow({
        professionalId,
        requested: Array.from(selected).map((tipo) => (
          isSncrNotificationTipo(tipo)
            ? { receita_tipo: tipo, quantidade: quantities[tipo] || 10 }
            : { receita_tipo: tipo }
        )),
        onStatus: setStatusText,
        signal: controller.signal,
      });
      setResult(status);
      if (status.status === 'failed' || status.status === 'expired') {
        setError(status.error_message || 'O SNCR não liberou a numeração.');
      }
      await load();
      if (status.status === 'completed' || status.status === 'partial') onCompleted?.(status);
    } catch (err: any) {
      if (!(err instanceof SncrFlowCancelled)) setError(err?.message || 'Falha ao obter a numeração.');
    } finally {
      setWorking(false);
      setStatusText(null);
      abortRef.current = null;
    }
  };

  const disabledConfig = overview && !overview.enabled;
  const professionalIssue = overview?.professional.issue;

  return (
    <Dialog open={open} onClose={() => !working && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: cloudApiDialogPaperSx }}>
      <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NumberIcon sx={cloudApiDialogHeaderIconSx} />
          Numeração SNCR · Anvisa
          {overview?.environment === 'hmg' && (
            <Chip size="small" label="Homologação" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.65rem' }} />
          )}
        </Box>
        <IconButton onClick={onClose} disabled={working} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ ...cloudApiDialogContentSx, p: 3 }}>
        <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate, mb: 2 }}>
          Receitas de controle especial e notificações de receita eletrônicas precisam de um número do SNCR
          para a farmácia registrar a dispensação. O pedido é feito com o gov.br do próprio prescritor.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {disabledConfig && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            A integração SNCR está desligada neste servidor. Fale com o suporte para ativar.
          </Alert>
        )}
        {professionalIssue && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            {professionalIssue} Ajuste em Profissionais antes de pedir numeração.
          </Alert>
        )}
        {result && result.status !== 'failed' && result.status !== 'expired' && (
          <Alert severity={result.status === 'completed' ? 'success' : 'warning'} sx={{ mb: 2, borderRadius: 2 }}>
            {(result.result || []).map((r) => (
              <div key={r.receita_tipo}>
                {SNCR_TIPO_LABELS[r.receita_tipo]}: {r.ok ? `${r.quantidade} número(s) recebidos` : r.error}
              </div>
            ))}
          </Alert>
        )}

        {loading && !overview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: CLOUD_API_THEME.teal }} />
          </Box>
        ) : overview ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: CLOUD_API_THEME.navy }}>
                {overview.professional.name}
                <Typography component="span" variant="caption" sx={{ ml: 1, color: CLOUD_API_THEME.slate }}>
                  {[overview.professional.conselho, overview.professional.documento, overview.professional.uf].filter(Boolean).join(' ')}
                </Typography>
              </Typography>
              <IconButton size="small" onClick={() => void load()} disabled={working || loading} aria-label="Atualizar saldo">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            <Stack spacing={1}>
              {SNCR_TIPOS.map((tipo) => {
                const balance = balanceOf.get(tipo);
                const reason = blockedReason(tipo);
                const checked = selected.has(tipo);
                return (
                  <Box
                    key={tipo}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.25,
                      borderRadius: 2,
                      border: `1px solid ${checked ? CLOUD_API_THEME.teal : CLOUD_API_THEME.borderSoft}`,
                      bgcolor: checked ? CLOUD_API_THEME.tealSoft : CLOUD_API_THEME.paper,
                      opacity: reason ? 0.6 : 1,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={Boolean(reason) || working || Boolean(disabledConfig) || Boolean(professionalIssue)}
                      onChange={() => toggle(tipo)}
                      sx={{ p: 0.25 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: CLOUD_API_THEME.navy }}>
                        {tipo} · {SNCR_TIPO_LABELS[tipo]}
                      </Typography>
                      <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block' }}>
                        {reason || SNCR_TIPO_HINTS[tipo]}
                      </Typography>
                    </Box>
                    {isSncrNotificationTipo(tipo) && checked && (
                      <TextField
                        select
                        size="small"
                        value={quantities[tipo] || 10}
                        onChange={(e) => setQuantities((q) => ({ ...q, [tipo]: Number(e.target.value) }))}
                        disabled={working}
                        sx={{ ...cloudApiInputFieldSx, width: 84 }}
                      >
                        {NR_QUANTITIES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                      </TextField>
                    )}
                    <Chip
                      size="small"
                      label={`${balance?.available ?? 0} disponíveis`}
                      sx={{
                        bgcolor: (balance?.available ?? 0) > 0 ? CLOUD_API_THEME.tealSoft : '#fee2e2',
                        color: (balance?.available ?? 0) > 0 ? CLOUD_API_THEME.tealDark : '#b91c1c',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>

            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: CLOUD_API_THEME.slateFaint }}>
              Primeiro acesso? O prescritor precisa entrar uma vez em sncr.anvisa.gov.br para ser cadastrado.
              Notificações (A, B, B2, retinoides, talidomida) só saem se a vigilância sanitária local já
              autorizou o saldo no SNCR.
            </Typography>
          </>
        ) : null}

        {working && statusText && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2 }}>
            <CircularProgress size={18} sx={{ color: CLOUD_API_THEME.teal }} />
            <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>{statusText}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ ...cloudApiDialogFooterSx, p: 3, gap: 1 }}>
        {working ? (
          <Button onClick={() => abortRef.current?.abort()} sx={cloudApiDialogCancelButtonSx}>
            Cancelar pedido
          </Button>
        ) : (
          <Button onClick={onClose} sx={cloudApiDialogCancelButtonSx}>
            Fechar
          </Button>
        )}
        <Button
          variant="contained"
          onClick={() => void handleRequest()}
          disabled={working || loading || !selected.size || Boolean(disabledConfig) || Boolean(professionalIssue)}
          startIcon={working ? <CircularProgress size={18} color="inherit" /> : <NumberIcon />}
          sx={cloudApiDialogPrimaryButtonSx}
        >
          {working ? 'Aguardando gov.br…' : 'Entrar com gov.br e obter numeração'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SncrNumberingDialog;
